import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { LuUserCog } from 'react-icons/lu';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselFooter,
  CarouselIndicator,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Form,
} from '@qodo/design-system';
import { useInitiateMTGithubInstallationMutation } from 'network/mutations/useInitiateMTGithubInstallationMutation';
import { ROUTES } from 'routes/consts';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { spawn } from 'utils/translations';
import { Image } from '../../Image';
import { INSTALL_GUIDE_CAROUSEL_STEPS } from '../configuration';
import { FORM_IDS } from '../consts';

export const InstallGuide = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const spawnInstallGuide = spawn(
    'dialogs.github-mt-installation.install-guide',
  );
  const { setIsPending } = useMultiStepDialog();
  const { mutateAsync: initiateInstallationMutateAsync } =
    useInitiateMTGithubInstallationMutation();
  const methods = useForm();

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    const onSelect = () => setCurrent(api.selectedScrollSnap());

    api.on('select', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  const onSubmit = async () => {
    setIsPending(true);

    try {
      const frontendRedirectUrl = `${window.location.origin}${ROUTES.GITHUB_MT_INSTALLATION_STEP_2}`;
      const response = await initiateInstallationMutateAsync({
        frontendRedirectUrl,
      });

      window.location.href = response.installationUrl;
    } catch {
      setIsPending(false);
    }
  };

  const carouselItems = INSTALL_GUIDE_CAROUSEL_STEPS.map((step) => {
    const title = spawnInstallGuide(`${step.id}.title`);

    return (
      <CarouselItem key={step.id} className="basis-full">
        <Image
          source={step.image}
          alt={title}
          title={title}
          className="w-full rounded-lg"
        />
      </CarouselItem>
    );
  });

  const carouselIndicators = INSTALL_GUIDE_CAROUSEL_STEPS.map((step, index) => (
    <CarouselIndicator
      key={step.id}
      index={index}
      isCurrent={index === current}
      className="font-mono text-xs uppercase"
    >
      {index + 1}. {spawnInstallGuide(`${step.id}.title`)}
    </CarouselIndicator>
  ));

  return (
    <Step
      title={spawnInstallGuide('title')}
      description={spawnInstallGuide('description')}
    >
      <div className="flex items-center gap-2 text-sm text-text-bg-neutral-mid">
        <LuUserCog className="size-4 shrink-0" />
        {spawnInstallGuide('admin-note')}
      </div>
      <div className="bg-fill-interactive-neutral-idle rounded-lg flex flex-col gap-2 p-5">
        <Carousel setApi={setApi} className="w-full">
          <CarouselContent>{carouselItems}</CarouselContent>
          <CarouselFooter>
            <CarouselPrevious type="button" />
            <div className="flex items-center justify-center gap-4">
              {carouselIndicators}
            </div>
            <CarouselNext type="button" />
          </CarouselFooter>
        </Carousel>
      </div>
      <Form
        {...({ id: FORM_IDS.INSTALL_GUIDE } as Record<string, string>)}
        methods={methods}
        onSubmit={onSubmit}
      />
    </Step>
  );
};
